export interface LeaseData {
  msrp: number;
  sellingPrice: number;
  residualValue: number;
  moneyFactor: number;
  term: number;
  downPayment: number;
  taxes: number;
  fees: number;
}

export interface Finding {
  title: string;
  message: string;
  type: 'good' | 'negotiate' | 'warning';
}

export interface LeaseAnalysis {
  monthlyPayment: number;
  totalCost: number;
  effectiveAPR: number;
  findings: Finding[];
}

export function calculatePayments(data: LeaseData): number {
  // Calculate net capitalized cost
  const netCapCost = data.sellingPrice + data.fees - data.downPayment;
  
  // Calculate monthly depreciation
  const monthlyDepreciation = (netCapCost - data.residualValue) / data.term;
  
  // Calculate monthly rent charge (interest)
  const rentCharge = (netCapCost + data.residualValue) * data.moneyFactor;
  
  // Fixed monthly sales tax
  const monthlyTax = 110.88;
  
  // Total monthly payment
  return monthlyDepreciation + rentCharge + monthlyTax;
}

// Convert APR to money factor
export function aprToMoneyFactor(apr: number): number {
  return apr / 2400;
}

// Convert money factor to APR
export function moneyFactorToAPR(moneyFactor: number): number {
  return moneyFactor * 2400;
}

export function analyzeTerms(data: LeaseData): LeaseAnalysis {
  const findings: Finding[] = [];
  const monthlyPayment = calculatePayments(data);
  const totalCost = (monthlyPayment * data.term) + data.downPayment;
  const effectiveAPR = moneyFactorToAPR(data.moneyFactor);

  // Analyze selling price vs MSRP
  const priceToMsrpRatio = data.sellingPrice / data.msrp;
  if (priceToMsrpRatio > 0.95) {
    findings.push({
      title: 'High Selling Price',
      message: `The selling price is ${((priceToMsrpRatio - 1) * 100).toFixed(1)}% of MSRP. Consider negotiating a lower price.`,
      type: 'warning'
    });
  } else if (priceToMsrpRatio <= 0.90) {
    findings.push({
      title: 'Good Selling Price',
      message: `You're getting ${((1 - priceToMsrpRatio) * 100).toFixed(1)}% off MSRP, which is a favorable discount.`,
      type: 'good'
    });
  } else {
    findings.push({
      title: 'Average Selling Price',
      message: 'The selling price is within normal range, but you might be able to negotiate further.',
      type: 'negotiate'
    });
  }

  // Analyze dealer add-ons and fees
  if (data.fees > 2000) {
    findings.push({
      title: 'Excessive Dealer Add-ons',
      message: 'Dealer add-ons and protection packages are high-profit items that can be negotiated or declined.',
      type: 'warning'
    });
  } else if (data.fees < 1000) {
    findings.push({
      title: 'Reasonable Fees',
      message: 'The total fees are within an acceptable range.',
      type: 'good'
    });
  } else {
    findings.push({
      title: 'Moderate Fees',
      message: 'Consider negotiating some of the dealer add-ons and fees.',
      type: 'negotiate'
    });
  }

  // Analyze down payment
  if (data.downPayment > data.sellingPrice * 0.2) {
    findings.push({
      title: 'High Down Payment',
      message: 'Large down payments on leases are generally not recommended as you risk losing this money if the vehicle is totaled.',
      type: 'warning'
    });
  } else if (data.downPayment < data.sellingPrice * 0.1) {
    findings.push({
      title: 'Low Down Payment',
      message: 'A low down payment is generally good for a lease, minimizing your risk while maintaining reasonable monthly payments.',
      type: 'good'
    });
  }

  // Analyze money factor
  const averageMoneyFactor = aprToMoneyFactor(6.0); // 6% APR as baseline
  if (data.moneyFactor > averageMoneyFactor) {
    const difference = (moneyFactorToAPR(data.moneyFactor) - moneyFactorToAPR(averageMoneyFactor)).toFixed(2);
    findings.push({
      title: 'High Money Factor',
      message: `The money factor (${moneyFactorToAPR(data.moneyFactor).toFixed(2)}% APR) is ${difference}% higher than average. Request the buy rate from the lender.`,
      type: 'warning'
    });
  } else if (data.moneyFactor <= averageMoneyFactor) {
    findings.push({
      title: 'Competitive Money Factor',
      message: `The money factor (${moneyFactorToAPR(data.moneyFactor).toFixed(2)}% APR) is at or below market rates.`,
      type: 'good'
    });
  }

  // Analyze residual value
  const expectedResidualRatio = data.term <= 36 ? 0.55 : 0.45;
  const actualResidualRatio = data.residualValue / data.msrp;
  if (actualResidualRatio < expectedResidualRatio) {
    findings.push({
      title: 'Low Residual Value',
      message: `The residual value (${(actualResidualRatio * 100).toFixed(1)}% of MSRP) is lower than expected for a ${data.term}-month lease.`,
      type: 'warning'
    });
  } else if (actualResidualRatio > expectedResidualRatio + 0.05) {
    findings.push({
      title: 'High Residual Value',
      message: `The residual value (${(actualResidualRatio * 100).toFixed(1)}% of MSRP) is higher than average, which helps reduce your monthly payments.`,
      type: 'good'
    });
  } else {
    findings.push({
      title: 'Average Residual Value',
      message: `The residual value is within normal range for a ${data.term}-month lease.`,
      type: 'negotiate'
    });
  }

  // Analyze acquisition fee
  if (data.fees > 1095) {
    findings.push({
      title: 'High Acquisition Fee',
      message: 'The acquisition fee is above average. This fee can sometimes be negotiated or rolled into the monthly payments.',
      type: 'negotiate'
    });
  }

  // Analyze monthly depreciation
  const monthlyDepreciation = (data.sellingPrice - data.residualValue) / data.term;
  if (monthlyDepreciation > (data.msrp * 0.01)) {
    findings.push({
      title: 'High Depreciation Rate',
      message: 'The monthly depreciation is higher than average, indicating either an inflated selling price or low residual value.',
      type: 'warning'
    });
  } else if (monthlyDepreciation < (data.msrp * 0.008)) {
    findings.push({
      title: 'Low Depreciation Rate',
      message: 'The monthly depreciation is lower than average, which is favorable for your lease terms.',
      type: 'good'
    });
  }

  return {
    monthlyPayment,
    totalCost,
    effectiveAPR,
    findings
  };
}

export function generateGoodLeaseTerms(msrp: number): LeaseData {
  // Calculate optimal lease terms based on MSRP
  return {
    msrp: msrp,
    sellingPrice: msrp * 0.92, // 8% below MSRP (good negotiation)
    residualValue: msrp * 0.60, // 60% residual (strong for 36-month lease)
    moneyFactor: 0.00200, // 4.8% APR (competitive rate)
    term: 36, // Standard 3-year lease
    downPayment: Math.min(msrp * 0.045, 2500), // 4.5% down or $2,500, whichever is less
    taxes: 0, // Will be included in monthly payment
    fees: 895, // Standard acquisition fee
  };
}